// Discord and Facebook, in the platforms' own colours so they stand out from
// the gold UI. The links come from the public config (admin > Site settings);
// one that is not set is simply not shown.
import type { ComponentType } from 'react';
import { useConfig } from '@/lib/session';
import { IconDiscordMark, IconFacebookMark } from './icons';
import { cx } from './ui';

type Social = { key: string; href: string; label: string; cta: string; Icon: ComponentType<{ size?: number }>; tone: string };

const safeUrl = (u: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);

export function useSocials(): Social[] {
  const { discordInvite, facebookUrl } = useConfig();
  const list: Social[] = [];
  const discord = safeUrl(discordInvite);
  if (discord) list.push({ key: 'discord', href: discord, label: 'Discord', cta: 'Join our Discord', Icon: IconDiscordMark, tone: 'bg-discord' });
  const facebook = safeUrl(facebookUrl);
  if (facebook) list.push({ key: 'facebook', href: facebook, label: 'Facebook', cta: 'Follow on Facebook', Icon: IconFacebookMark, tone: 'bg-facebook' });
  return list;
}

const brandButton = 'cut-sm inline-flex items-center justify-center text-white transition-[filter] duration-150 hover:brightness-115';

/** Compact logo buttons for the header, so they are on every page, phones included. */
export function SocialIcons({ className }: { className?: string }) {
  const socials = useSocials();
  if (!socials.length) return null;
  return (
    <div className={cx('flex items-center gap-2', className)}>
      {socials.map(({ key, href, label, Icon, tone }) => (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className={cx(brandButton, 'h-9 w-9', tone)}>
          <Icon size={18} />
        </a>
      ))}
    </div>
  );
}

/** Labelled buttons: the home hero and community panel, the phone menu, the footer. */
export function SocialButtons({ size = 'md', className }: { size?: 'sm' | 'md'; className?: string }) {
  const socials = useSocials();
  if (!socials.length) return null;
  return (
    <div className={cx('flex flex-wrap gap-3', className)}>
      {socials.map(({ key, href, cta, Icon, tone }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            brandButton,
            'whitespace-nowrap font-semibold uppercase tracking-[0.12em]',
            size === 'sm' ? 'h-9 gap-1.5 px-3.5 text-[0.72rem]' : 'h-11 gap-2 px-5 text-[0.8rem]',
            tone,
          )}
        >
          <Icon size={size === 'sm' ? 16 : 18} />
          {cta}
        </a>
      ))}
    </div>
  );
}
